import React, { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useLanguage } from '../contexts/LanguageContext';
import { useAuth } from '../contexts/AuthContext';
import { Button } from './ui/button';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from './ui/dropdown-menu';
import { Menu, X, User, LogOut, LayoutDashboard, Shield, Globe, Home, Grid, Info } from 'lucide-react';

const Header = () => {
  const { language, toggleLanguage, t } = useLanguage();
  const { user, isAuthenticated, isAdmin, logout } = useAuth();
  const navigate = useNavigate();
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);

  const handleLogout = () => {
    logout();
    navigate('/');
  };

  // Check if user is admin
  const userIsAdmin = user?.role === 'admin';

  return (
    <header className="sticky top-0 z-50 bg-white/95 backdrop-blur-md border-b border-slate-200 shadow-lg transition-all duration-300">
      <div className="container mx-auto px-4 md:px-6">
        <div className="flex items-center justify-between h-16">
          {/* Logo */}
          <Link to="/" className="flex items-center gap-2 group" data-testid="header-logo">
            <span className="text-xl md:text-2xl font-extrabold text-blue-700 font-['Manrope'] transform group-hover:scale-105 transition-transform duration-300" style={{ fontStyle: 'italic' }}>
              {t('brand')}
            </span>
            <span className="text-xl md:text-2xl font-extrabold bg-gradient-to-r from-[#E85A24] to-orange-600 bg-clip-text text-transparent font-['Manrope'] transform group-hover:scale-105 transition-transform duration-300">
              {t('brandLocation')}
            </span>
          </Link>

          {/* Desktop Navigation */}
          <nav className="hidden md:flex items-center gap-2">
            {/* Navigation Links */}
            <Link to="/">
              <Button
                variant="ghost"
                size="sm"
                className="flex items-center gap-2 btn-scale hover:bg-blue-50"
                data-testid="home-link"
              >
                <Home className="h-4 w-4 text-blue-600" />
                <span className="font-semibold">{language === 'ta' ? 'முகப்பு' : 'Home'}</span>
              </Button>
            </Link>

            <Link to="/categories">
              <Button
                variant="ghost"
                size="sm"
                className="flex items-center gap-2 btn-scale hover:bg-blue-50"
                data-testid="categories-link"
              >
                <Grid className="h-4 w-4 text-blue-600" />
                <span className="font-semibold">{language === 'ta' ? 'வகைகள்' : 'Categories'}</span>
              </Button>
            </Link>

            <Link to="/about">
              <Button
                variant="ghost"
                size="sm"
                className="flex items-center gap-2 btn-scale hover:bg-blue-50"
                data-testid="about-link"
              >
                <Info className="h-4 w-4 text-blue-600" />
                <span className="font-semibold">{language === 'ta' ? 'எங்களை பற்றி' : 'About Us'}</span>
              </Button>
            </Link>

            {/* Language Toggle */}
            <Button
              variant="ghost"
              size="sm"
              onClick={toggleLanguage}
              className="flex items-center gap-2 btn-scale hover:bg-blue-50"
              data-testid="language-toggle"
            >
              <Globe className="h-4 w-4 text-blue-600" />
              <span className="font-semibold">{language === 'en' ? 'தமிழ்' : 'English'}</span>
            </Button>

            {isAuthenticated() && (
              <>
                {/* Admin Panel Link - Direct access for admins */}
                {userIsAdmin && (
                  <Link to="/admin">
                    <Button 
                      variant="outline"
                      size="sm"
                      className="flex items-center gap-2 border-purple-300 text-purple-700 hover:bg-purple-50 btn-scale font-semibold shadow-sm"
                      data-testid="admin-panel-btn"
                    >
                      <Shield className="h-4 w-4" />
                      {t('admin')}
                    </Button>
                  </Link>
                )}

                <DropdownMenu>
                  <DropdownMenuTrigger asChild>
                    <Button variant="ghost" size="sm" className="flex items-center gap-2 hover:bg-slate-100 btn-scale" data-testid="user-menu-trigger">
                      <div className="w-8 h-8 rounded-full bg-gradient-to-br from-blue-500 to-purple-600 flex items-center justify-center">
                        <User className="h-5 w-5 text-white" />
                      </div>
                      <span className="hidden lg:inline font-semibold">{user?.name}</span>
                    </Button>
                  </DropdownMenuTrigger>
                  <DropdownMenuContent align="end" className="w-48 shadow-xl border-slate-200">
                    <DropdownMenuItem onClick={() => navigate('/dashboard')} data-testid="dashboard-link" className="cursor-pointer hover:bg-blue-50">
                      <LayoutDashboard className="mr-2 h-4 w-4 text-blue-600" />
                      <span className="font-medium">{t('dashboard')}</span>
                    </DropdownMenuItem>
                    {userIsAdmin && (
                      <DropdownMenuItem onClick={() => navigate('/admin')} data-testid="admin-link" className="cursor-pointer hover:bg-purple-50">
                        <Shield className="mr-2 h-4 w-4 text-purple-600" />
                        <span className="font-medium">{t('admin')}</span>
                      </DropdownMenuItem>
                    )}
                    <DropdownMenuSeparator />
                    <DropdownMenuItem onClick={handleLogout} data-testid="logout-btn" className="cursor-pointer hover:bg-red-50 text-red-600">
                      <LogOut className="mr-2 h-4 w-4" />
                      <span className="font-medium">{t('logout')}</span>
                    </DropdownMenuItem>
                  </DropdownMenuContent>
                </DropdownMenu>
              </>
            )}
          </nav>

          {/* Mobile Menu Button */}
          <button
            className="md:hidden p-2 rounded-lg hover:bg-slate-100 transition-all duration-300 btn-scale"
            onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
            data-testid="mobile-menu-btn"
          >
            {mobileMenuOpen ? 
              <X className="h-6 w-6 text-slate-700 transform rotate-90 transition-transform duration-300" /> : 
              <Menu className="h-6 w-6 text-slate-700 transition-transform duration-300" />
            }
          </button>
        </div>

        {/* Mobile Menu */}
        {mobileMenuOpen && (
          <div className="md:hidden py-4 border-t border-slate-200 animate-fadeIn bg-gradient-to-b from-white to-slate-50" data-testid="mobile-menu">
            <div className="flex flex-col gap-3">
              {/* Navigation Links */}
              <Link to="/" onClick={() => setMobileMenuOpen(false)}>
                <Button variant="ghost" className="w-full justify-start hover:bg-blue-50 btn-scale">
                  <Home className="mr-2 h-5 w-5 text-blue-600" />
                  <span className="font-semibold">{language === 'ta' ? 'முகப்பு' : 'Home'}</span>
                </Button>
              </Link>

              <Link to="/categories" onClick={() => setMobileMenuOpen(false)}>
                <Button variant="ghost" className="w-full justify-start hover:bg-blue-50 btn-scale">
                  <Grid className="mr-2 h-5 w-5 text-blue-600" />
                  <span className="font-semibold">{language === 'ta' ? 'வகைகள்' : 'Categories'}</span>
                </Button>
              </Link>

              <Link to="/about" onClick={() => setMobileMenuOpen(false)}>
                <Button variant="ghost" className="w-full justify-start hover:bg-blue-50 btn-scale">
                  <Info className="mr-2 h-5 w-5 text-blue-600" />
                  <span className="font-semibold">{language === 'ta' ? 'எங்களை பற்றி' : 'About Us'}</span>
                </Button>
              </Link>

              <div className="border-t border-slate-200 my-2"></div>

              {/* Language Toggle */}
              <Button
                variant="ghost"
                onClick={toggleLanguage}
                className="justify-start hover:bg-blue-50 btn-scale"
              >
                <Globe className="mr-2 h-5 w-5 text-blue-600" />
                <span className="font-semibold">{language === 'en' ? 'தமிழ்' : 'English'}</span>
              </Button>

              {isAuthenticated() && (
                <>
                  <div className="border-t border-slate-200 my-2"></div>
                  
                  {userIsAdmin && (
                    <Link to="/admin" onClick={() => setMobileMenuOpen(false)}>
                      <Button variant="outline" className="w-full justify-start text-purple-700 border-purple-300 hover:bg-purple-50 btn-scale font-semibold">
                        <Shield className="mr-2 h-5 w-5" />
                        {t('admin')}
                      </Button>
                    </Link>
                  )}
                  <Link to="/dashboard" onClick={() => setMobileMenuOpen(false)}>
                    <Button variant="ghost" className="w-full justify-start hover:bg-blue-50 btn-scale">
                      <LayoutDashboard className="mr-2 h-5 w-5 text-blue-600" />
                      <span className="font-semibold">{t('dashboard')}</span>
                    </Button>
                  </Link>
                  <Button variant="ghost" className="w-full justify-start hover:bg-red-50 text-red-600 btn-scale" onClick={handleLogout}>
                    <LogOut className="mr-2 h-5 w-5" />
                    <span className="font-semibold">{t('logout')}</span>
                  </Button>
                </>
              )}
            </div>
          </div>
        )}
      </div>
    </header>
  );
};

export default Header;
